import { RudderAnalytics } from '@rudderstack/analytics-js';
import { captureException } from '@sentry/nextjs';
import getConfig from 'next/config';

import { debug } from '@/src/domains/shared/general';

const {
  publicRuntimeConfig: { RUDDERSTACK_WRITE_KEY, RUDDERSTACK_DATAPLANE_URL, RUDDERSTACK_ENABLED },
} = getConfig();

const canBeEnabled = () => typeof window !== 'undefined' && RUDDERSTACK_ENABLED;

let rudderanalytics;
let isInitialized = false;

if (canBeEnabled()) {
  // eslint-disable-next-line global-require
  rudderanalytics = new RudderAnalytics();
}

const isActive = () => canBeEnabled() && typeof rudderanalytics !== 'undefined';

/**
 * Track a page view
 * @param {PageViewProps} params - Page view parameters
 */
export function trackPageView({ category = '', name = '', properties = {} } = {}) {
  if (!isActive()) {
    debug('RudderStack::Not active - skipping page view tracking');
    return;
  }

  try {
    rudderanalytics.page(category, name, properties, (err) => {
      if (err) {
        debug(`RudderStack::Error tracking page view | category: ${category} name: ${name}`, err);
        captureException(err);
      } else {
        debug(
          `RudderStack::Page view tracked successfully | category: ${category} name: ${name}`,
          properties
        );
      }
    });
  } catch (e) {
    debug(`RudderStack::Error in trackPageView | category: ${category} name: ${name}`, e);
    captureException(e);
  }
}

/**
 * Initialize RudderStack
 */
export function initRudderStack() {
  if (!isActive()) {
    debug('RudderStack::Not active - skipping initialization');
    return;
  }

  if (isInitialized) {
    debug('RudderStack::Already initialized - skipping');
    return;
  }

  try {
    rudderanalytics.load(RUDDERSTACK_WRITE_KEY, RUDDERSTACK_DATAPLANE_URL, {
      useServerSideCookies: true,
      integrations: { All: true },
      storage: {
        type: 'localStorage',
      },
      consentManagement: {
        enabled: true,
        provider: 'custom',
      },
      preConsent: {
        enabled: true,
        storage: {
          strategy: 'session',
        },
        events: {
          delivery: 'buffer',
        },
      },
    });

    rudderanalytics.ready(() => {
      isInitialized = true;
      debug('RudderStack::Successfully loaded and ready');
    });
  } catch (e) {
    debug('RudderStack::Error - Unable to load', e);
    captureException(e);
  }
}

/**
 * Update RudderStack consent settings
 * @param {boolean} isAllowed - Whether analytics consent is allowed
 * @param {boolean} shouldTrack - Whether to track the consent change event
 */
export function updateRudderStackConsent(isAllowed = false, shouldTrack = true) {
  if (!isActive()) {
    debug('RudderStack::Not active - skipping consent update');
    return;
  }

  try {
    const consentConfig = {
      consentManagement: {
        allowedConsentIds: isAllowed ? ['analytics'] : [],
        deniedConsentIds: isAllowed ? [] : ['analytics'],
      },
      trackConsent: shouldTrack,
      discardPreConsentEvents: true,
      storage: {
        type: isAllowed ? 'cookieStorage' : 'none',
      },
    };

    rudderanalytics.consent(consentConfig);
    debug(`RudderStack::Consent updated successfully | config:`, consentConfig);
  } catch (e) {
    debug(`RudderStack::Error updating consent | allowed: ${isAllowed}`, e);
    captureException(e);
  }
}

/**
 * Track a custom event
 * @param {EventProps} event - The event to track
 * @param {Object} props - Additional properties
 */
export function trackEvent(event, props = {}) {
  if (!isActive()) {
    debug('RudderStack::Not active - skipping event tracking');
    return;
  }

  try {
    const { name, eventProps } = event || {};
    const trackingProps = { ...eventProps, ...props };

    rudderanalytics.track(name, trackingProps, (err) => {
      if (err) {
        debug(`RudderStack::Error tracking event | name: ${name}`, err);
        captureException(err);
      } else {
        debug(`RudderStack::Event tracked successfully | name: ${name} props:`, trackingProps);
      }
    });
  } catch (e) {
    debug(`RudderStack::Error in trackEvent | event:`, event, e);
    captureException(e);
  }
}
